/**
 * Phase 4.1 — Waiter anchor warning helper.
 *
 * Doctrine:
 *   - Bucket 2 §2.3 Anchor Ticket Definition
 *   - Bucket 2 §2.8.5 Waiter AutoBump
 *   - Bucket 2 §2.8.6 Waiter Anchor Warning — "If a waiter ticket reaches
 *     its scheduled appointment time and is not the first eligible anchor
 *     ticket in the assigned technician lane/chain, OptiBay must warn the
 *     user."
 *   - Bucket 2 §2.10  Waiter Rules
 *   - Bucket 4.5 §4.5.5 Constraint Precedence — Due Out outranks Waiter Flex
 *
 * Why this module exists
 * ----------------------
 * The existing `detectWaiterPromiseAtRisk` (in
 * `supabase/functions/_shared/boardIssueDetector.ts`) is delay-based: it
 * fires when `actualStartMin - scheduledMin > flexMin`. That correctly
 * catches waiters whose `start_time` has already been bumped past flex
 * by cascade/AutoBump — the FLEX VIOLATION case.
 *
 * What it MISSES is the ANCHOR-POSITION case per Bucket 2 §2.8.6: a
 * waiter whose scheduled appointment time has arrived (or passed) BUT
 * whose `start_time` has not yet been mutated, because the waiter is
 * physically behind another in-bay ticket. The waiter is not the lane
 * anchor, but no delay shows in start_time yet. The user needs to be
 * warned NOW so they can intervene before the flex window expires.
 *
 * This module provides the canonical helper for that anchor-position
 * detection. Phase 4.1 scope: HELPER + TESTS ONLY. No detector wiring;
 * Catalyst auto-resolution is Phase 4.2. The helper is ready for a
 * future detector adoption that composes anchor warning alongside the
 * existing flex-violation detection.
 *
 * Risk-state model
 * ----------------
 * Three discrete states per ticket:
 *
 *   - 'no_risk'        — waiter is the anchor, OR not due yet, OR not a
 *                        waiter, OR in a terminal/inactive state, OR no
 *                        appointment baseline.
 *   - 'anchor_warning' — waiter is due now AND not the lane anchor AND
 *                        within the flex window. The early-warning state
 *                        Bucket 2 §2.8.6 says Core MUST surface.
 *   - 'flex_violation' — waiter is past `scheduledStartTime + flexMin`.
 *                        Higher severity. Mirrors the existing
 *                        `detectWaiterPromiseAtRisk` condition.
 *
 * Doctrine precedence reminder
 * ----------------------------
 * Bucket 4.5 §4.5.5: Due Out outranks Waiter Flex. RESOLUTION_PRIORITY
 * (Phase 0.1) places `DUE_OUT_MISS` before `WAITER_PROMISE_AT_RISK`. This
 * helper does NOT change that order — it only adds detection vocabulary.
 *
 * Builds on:
 *   - Phase 1.1 `getProductionState`.
 *   - Phase 1.3 `isProductionRelevantState`.
 *   - Phase 1.4 `canPerformActiveProductionMutation` (gate for terminal
 *     waiters that should never warn).
 */

import {
  getProductionState,
  isProductionRelevantState,
  canPerformActiveProductionMutation,
  type ProductionStateTicketShape,
} from './ticketProductionState';

// =============================================================================
// Input types
// =============================================================================

/**
 * Permissive shape for a waiter-evaluation ticket. Extends the canonical
 * production-state shape with the additional fields needed for the
 * anchor-position decision.
 */
export interface WaiterTicketShape extends ProductionStateTicketShape {
  /** Ticket type. `'Waiter'` indicates a waiter ticket. */
  type?: string | null;
  /**
   * Original waiter appointment baseline (the promise time). Bucket 2
   * §2.8.5: "the original waiter appointment time must remain auditable
   * as the promise baseline." This is the immutable reference, NOT
   * `start_time` (which may be bumped by AutoBump).
   */
  scheduled_start_time?: string | Date | null;
  /**
   * Current scheduled start (may differ from `scheduled_start_time` after
   * AutoBump / cascade mutations).
   */
  start_time?: string | Date | null;
  /** Tech assignment used to scope the lane. */
  assigned_tech_id?: string | null;
}

/**
 * The three discrete waiter-flex risk states. See module docstring for
 * full semantics.
 */
export type WaiterFlexRiskState = 'no_risk' | 'anchor_warning' | 'flex_violation';

// =============================================================================
// Internal helpers
// =============================================================================

/** Parse an ISO/Date/number into a millisecond timestamp; null on bad input. */
function parseTs(value: string | Date | number | null | undefined): number | null {
  if (value == null) return null;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isFinite(t) ? t : null;
  }
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Pre-filter for "lane-eligible" tickets: only those in an active
 * production state participate in anchor determination. Terminal /
 * inactive rows (deleted, archived, picked_up, completed_awaiting_pickup,
 * parking_lot_inactive, estimate_prospect) are not part of the lane's
 * ordering for the purposes of anchor selection.
 */
function isLaneEligible(ticket: WaiterTicketShape): boolean {
  return isProductionRelevantState(getProductionState(ticket));
}

// =============================================================================
// Public predicates
// =============================================================================

/** Is this ticket a waiter (`type === 'Waiter'`)? Case-sensitive match. */
export function isWaiterTicket(ticket: WaiterTicketShape): boolean {
  return ticket.type === 'Waiter';
}

/**
 * Has this waiter's scheduled appointment time arrived (relative to `now`)?
 *
 * Returns true iff `now >= scheduled_start_time`. Defensive against
 * missing/invalid timestamps (returns false).
 *
 * The optional `_flexMin` argument is reserved for future tuning (e.g.,
 * an "approaching" window) but is currently ignored — the helper fires
 * the moment the appointment time arrives.
 */
export function isWaiterDueNow(
  ticket: WaiterTicketShape,
  now: Date,
  _flexMin?: number,
): boolean {
  if (!isWaiterTicket(ticket)) return false;
  const scheduledMs = parseTs(ticket.scheduled_start_time);
  if (scheduledMs == null) return false;
  const nowMs = now.getTime();
  if (!Number.isFinite(nowMs)) return false;
  return nowMs >= scheduledMs;
}

/**
 * Return the first eligible (production-relevant) ticket in a lane,
 * sorted by `start_time` ascending.
 *
 * Caller is responsible for passing the LANE subset — i.e., tickets
 * already filtered by `assigned_tech_id === techId`. The helper does
 * not re-filter by tech.
 *
 * Returns `null` if no eligible tickets exist.
 */
export function getLaneAnchorTicket<T extends WaiterTicketShape>(
  laneTickets: ReadonlyArray<T>,
): T | null {
  const eligible = laneTickets
    .filter(isLaneEligible)
    .map(t => ({ t, ms: parseTs(t.start_time) }))
    .filter(x => x.ms != null)
    .sort((a, b) => (a.ms as number) - (b.ms as number));
  return eligible.length > 0 ? eligible[0].t : null;
}

/**
 * Is this waiter the anchor (first eligible) ticket in its lane?
 *
 * Per Bucket 2 §2.3 / §2.8.6: the anchor is the FIRST eligible ticket
 * (sorted by `start_time`). A waiter at position 0 is the anchor and
 * not at risk of being blocked.
 *
 * If `ticket` itself is not lane-eligible, this returns false.
 *
 * Caller passes the lane subset (same tech). The helper does not
 * re-filter by tech.
 */
export function isWaiterAnchorOrNextViable<T extends WaiterTicketShape>(
  ticket: T,
  laneTickets: ReadonlyArray<T>,
): boolean {
  if (!isLaneEligible(ticket)) return false;
  const anchor = getLaneAnchorTicket(laneTickets);
  if (!anchor) return false;
  return anchor === ticket || (
    'id' in ticket && 'id' in anchor && (ticket as any).id != null &&
    (anchor as any).id === (ticket as any).id
  );
}

/**
 * Compute the waiter flex-risk state for a ticket.
 *
 * Returns:
 *   - 'flex_violation' — past `scheduledStartTime + flexMin`. Highest
 *                        severity (sister to existing
 *                        `detectWaiterPromiseAtRisk`'s `delayMin > flexMin`).
 *   - 'anchor_warning' — due now (>= scheduledStartTime) AND not yet
 *                        past flex AND not the lane anchor.
 *   - 'no_risk'        — anything else (not a waiter, not due yet,
 *                        anchor, terminal, missing baseline, etc.).
 *
 * The `laneTickets` parameter is required for the anchor check. Caller
 * provides the lane subset (same tech). If `laneTickets` is empty, the
 * helper defaults to `'flex_violation'` when past flex and `'no_risk'`
 * otherwise — without a lane, no anchor decision is possible.
 */
export function getWaiterFlexRiskState<T extends WaiterTicketShape>(
  ticket: T,
  now: Date,
  flexMin: number,
  laneTickets?: ReadonlyArray<T>,
): WaiterFlexRiskState {
  if (!isWaiterTicket(ticket)) return 'no_risk';
  // Skip terminal/inactive waiters entirely.
  if (!canPerformActiveProductionMutation(ticket)) return 'no_risk';

  const scheduledMs = parseTs(ticket.scheduled_start_time);
  if (scheduledMs == null) return 'no_risk';

  const nowMs = now.getTime();
  if (!Number.isFinite(nowMs)) return 'no_risk';

  const safeFlexMin =
    Number.isFinite(flexMin) && flexMin >= 0 ? flexMin : 0;

  const flexBoundaryMs = scheduledMs + safeFlexMin * 60_000;

  // Past flex window → violation regardless of lane composition.
  if (nowMs > flexBoundaryMs) return 'flex_violation';

  // Not yet at scheduled appointment time → no risk.
  if (nowMs < scheduledMs) return 'no_risk';

  // Due now, still within flex window. Check anchor position.
  if (laneTickets && laneTickets.length > 0) {
    if (isWaiterAnchorOrNextViable(ticket, laneTickets)) return 'no_risk';
    return 'anchor_warning';
  }

  // No lane context → conservative no_risk (caller is expected to pass
  // the lane subset if they want anchor warning).
  return 'no_risk';
}

/**
 * Convenience: should the user be warned that this waiter is not the
 * lane anchor right now?
 *
 * Returns true ONLY for the `'anchor_warning'` state. The
 * `'flex_violation'` state is handled by the existing
 * `detectWaiterPromiseAtRisk` (delay-based detection). This helper is
 * specifically for the anchor-position warning that the existing
 * detector misses.
 */
export function shouldWarnWaiterNotAnchor<T extends WaiterTicketShape>(
  ticket: T,
  laneTickets: ReadonlyArray<T>,
  now: Date,
  flexMin: number,
): boolean {
  return getWaiterFlexRiskState(ticket, now, flexMin, laneTickets) === 'anchor_warning';
}
